import React, { useEffect } from 'react';
import useModal from '@/hooks/useModal';
import { Button, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import AppRoutes from '@/const/routes';
import { useRouter } from 'next/router';
import { UserI } from '@/interfaces';
import { useHasAdvancedCase } from '@/hooks/queries/useHasAdvancedCase';

interface Props {
	user: UserI | undefined;
}

export const ModalRestriction: React.FC<Props> = ({ user }) => {
	const router = useRouter();
	const pathname = router.pathname;

	const { Modal, show, hide } = useModal();

	const isRestrictedScreen = (
		[
			AppRoutes.CLIENTAPP_INBOX,
			AppRoutes.CLIENTAPP_FINANCIAL_RECOVERY,
		] as string[]
	).includes(pathname);
	const needsVerification =
		!!user && user.identity_verification_passed !== true;

	const { data: hasAdvancedCase, isLoading: isCheckingCases } =
		useHasAdvancedCase(needsVerification && isRestrictedScreen);

	useEffect(() => {
		if (
			needsVerification &&
			isRestrictedScreen &&
			!isCheckingCases &&
			!hasAdvancedCase
		) {
			show();
		} else {
			hide();
		}
	}, [needsVerification, isRestrictedScreen, isCheckingCases, hasAdvancedCase]);

	const handleGoToDashboard = () => {
		router.push(
			{
				pathname: AppRoutes.CLIENTAPP_DASHBOARD,
			},
			undefined,
			{ shallow: true }
		);
	};

	return (
		<Modal
			avoidClose
			backgroundColor="white"
			classNameCardContainer="w-full md:!w-[532px] !rounded-[32px]"
			classNameTitle="flex items-center justify-start gap-2 !mb-8 text-left"
			title={
				<>
					<Icon
						src={Icons.circleGreenExclamation}
						width={75}
						height={32}
						className="shrink-0"
					/>
					<span>Verify your identity to begin sending messages</span>
				</>
			}
		>
			<div className="flex flex-col items-center text-center gap-4 pb-2">
				<Typography type="regular-16" className="!text-gray-0">
					You&apos;ll need to verify your identity first before you can message
					your lawyer
				</Typography>
				<Button
					label="Go to dashboard"
					decoration="primary"
					size="fit"
					onClick={handleGoToDashboard}
					className="mt-2"
				/>
			</div>
		</Modal>
	);
};
